<!--
// app/components/Shared/stats/CardPlayerMvp.vue
-->
<script setup lang="ts">
import type { RankingsData } from "~/src/player/domain/Player";
import { useImages } from "~/app/composables/images";
import { useRegions } from "~/app/composables/regions";
import { setAliasUrl } from "~/src/Shared/utils";
import PlaceholderTeamShield from "~/app/components/pages/region-stats/cards-featured-stats/PlaceholderTeamShield.vue";

type Props = {
  seasonId: number | string;
  pageStatsLink: string;
  tableStatTitle: string;
  hasPartner?: boolean;
  partnerLogo?: string;
  partnerName?: string;
  isStats?: boolean; // Usage is in /stats
};

const props = defineProps<Props>();

const localePath = useLocalePath();
const route = useRoute();
const { regionParam, isRegionHome } = useRegions();
const { playerPlaceholderPath, videoBgTurnMvpPath, posterBgTurnMvpPath } = useImages();

const queries = computed(() => {
  return route.query ? route.query : undefined;
});

const videoBg = ref<HTMLVideoElement | null>(null);

let isPlaying = false;
let playPromise = null;

const playVideoBg = () => {
  if (videoBg.value && !isPlaying) {
    playPromise = videoBg.value.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          isPlaying = true;
        })
        .catch(error => {
          console.warn("play() failed:", error);
        });
    }
  }
};

const pauseVideoBg = () => {
  if (videoBg.value && isPlaying) {
    if (playPromise !== null) {
      playPromise.finally(() => {
        videoBg.value.pause();
        isPlaying = false;
      });
    } else {
      videoBg.value.pause();
      isPlaying = false;
    }
  }
};

const { data: topPlayer }: RankingsData[] = await useFetch(
  `/api/v1/competition/seasons/${props.seasonId}/players/stats/MVP`,
  {
    query: { page: 0, orderBy: "ranking", sortOrder: "ASC" },
    transform: input => {
      return input.slice(0, 3);
    },
  }
);
</script>

<template>
  <div
    class="flex flex-col max-w-[343px] w-full min-w-full lg:max-w-none 2xl:w-auto 2xl:aspect-[325/568]"
    @mouseenter="playVideoBg"
    @mouseleave="pauseVideoBg"
  >
    <nuxt-link
      :to="
        localePath({
          name: `region-stats-${pageStatsLink}`,
          params: { region: regionParam },
          query: queries,
        })
      "
      class="card-player-image-container"
      :no-prefetch="!!isRegionHome"
    >
      <video
        :src="videoBgTurnMvpPath"
        :poster="posterBgTurnMvpPath"
        class="absolute top-0 left-0 right-0 bottom-0 min-h-[463px] sm:min-h-[546px] object-cover object-top"
        ref="videoBg"
        width="100%"
        height="100%"
        loop
        muted
      ></video>
      <div class="image-container">
        <NuxtImg
          :src="
            topPlayer?.length && topPlayer[0]?.player?.image?.url
              ? setAliasUrl(topPlayer[0].player.image.url)
              : playerPlaceholderPath
          "
          :alt="topPlayer && topPlayer.length ? topPlayer[0]?.player?.shortName : ''"
          class="object-top object-cover min-h-[463px] sm:min-h-[546px]"
          width="456"
          height="642"
          sizes="256px sm:472px md:325px"
        />
      </div>
      <div class="gradient-container">
        <div class="h-full flex flex-col justify-start rtl:items-end gap-3">
          <div class="min-w-min min-h-fit flex justify-start items-start gap-1.5">
            <div class="flex items-start justify-start w-[15px]">
              <span class="turn-text">{{ $t("general.match.word") }}</span>
            </div>
          </div>

          <div class="text-primary">
            <svg
              width="52"
              height="219"
              viewBox="0 0 52 219"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="w-[42px] h-[168px] sm:w-auto sm:h-auto"
            >
              <path
                d="M51.9967 218.796L0 218.796L-1.08057e-06 194.075L22.5974 184.902C23.5043 184.488 24.6631 184.047 26.0739 183.581C27.4847 183.062 28.9458 182.57 30.4573 182.104C31.9185 181.585 33.2285 181.119 34.3873 180.704L34.3873 180.16C33.3293 179.849 32.0948 179.461 30.6841 178.994C29.2733 178.476 27.8626 177.958 26.4518 177.439C24.9906 176.869 23.7058 176.377 22.5974 175.962L-2.27328e-06 166.789L-3.32327e-06 142.768L51.9967 142.768L51.9967 158.549L25.167 158.549C24.1089 158.549 22.9249 158.549 21.6149 158.549C20.3049 158.497 19.0201 158.446 17.7605 158.394C16.5009 158.342 15.4176 158.316 14.5107 158.316L14.5107 158.938C15.468 159.249 16.6016 159.638 17.9116 160.104C19.2216 160.57 20.5316 161.037 21.8416 161.503C23.1012 161.918 24.2097 162.332 25.167 162.747L51.9967 173.941L51.9967 188.4L25.167 199.672C23.857 200.191 22.5218 200.709 21.1614 201.227C19.8011 201.694 18.5414 202.134 17.3826 202.549C16.1734 202.912 15.2161 203.222 14.5107 203.482L14.5107 204.103C15.2161 204.052 16.1482 204.026 17.307 204.026C18.4659 203.974 19.7255 203.948 21.0859 203.948C22.4462 203.896 23.8066 203.87 25.167 203.87L51.9967 203.87L51.9967 218.796Z"
                fill="currentColor"
              />
              <path
                d="M51.9967 109.385L-3.65776e-06 135.116L-4.42571e-06 117.547L25.3181 105.887C26.2754 105.369 27.4847 104.824 28.9458 104.254C30.3566 103.684 31.7925 103.088 33.2537 102.466C34.7148 101.844 36.0248 101.326 37.1837 100.912L37.1837 100.367C36.1256 99.9528 34.8912 99.4864 33.4804 98.9681C32.0696 98.3981 30.6589 97.8021 29.2481 97.1802C27.787 96.5583 26.477 95.9882 25.3181 95.4699L-5.90725e-06 83.6538L-6.64462e-06 66.7847L51.9967 92.5936L51.9967 109.385Z"
                fill="currentColor"
              />
              <path
                d="M51.9967 59.2211L-6.97524e-06 59.2211L-8.77619e-06 18.0201C-8.93023e-06 14.496 0.755756 11.4384 2.26728 8.84709C3.77882 6.20401 5.86977 4.15692 8.54014 2.70581C11.1601 1.20288 14.2084 0.451431 17.6849 0.451431C21.1614 0.45143 24.2601 1.20288 26.9808 2.70581C29.6512 4.20874 31.7421 6.28174 33.2537 8.92482C34.7148 11.5679 35.4454 14.6515 35.4454 18.1756L35.4454 43.4404L51.9967 43.4404L51.9967 59.2211ZM24.0333 43.4404L24.0333 22.5289C24.0333 20.1968 23.5043 18.4347 22.4462 17.2428C21.3378 15.999 19.7759 15.3771 17.7605 15.3771C16.4001 15.3771 15.2665 15.6621 14.3595 16.2322C13.4022 16.8022 12.6717 17.6055 12.1678 18.642C11.664 19.6785 11.4121 20.9742 11.4121 22.5289L11.4121 43.4404L24.0333 43.4404Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <NuxtImg
            v-if="hasPartner"
            :src="setAliasUrl(partnerLogo)"
            :alt="partnerName || ''"
            width="59"
            height="21"
            class="h-5 max-h-[21px] max-w-[40px] sm:max-w-[52px] object-contain"
          />
        </div>
      </div>

      <div class="mt-4 table-first-stats absolute z-10 min-w-max left-0 bottom-0 right-0 p-1">
        <div class="flex items-center justify-end text-gray-400 text-[8.5px] sm:text-[10px] uppercase px-1">
          <span class="w-1/6 text-center ml-auto min-w-min me-1">{{ $t("cards.stats.team.text") }}</span>
          <span class="w-4/6 text-left rtl:text-right">{{ $t("cards.stats.name.text") }}</span>
          <span class="w-1/6 text-center">{{ tableStatTitle }}</span>
        </div>
        <div
          v-for="(ranking, i) in topPlayer?.length ? topPlayer : 3"
          :key="ranking.id || i"
          class="flex items-center justify-end text-sm font-bold px-1 bg-gray-800 bg-opacity-50 rounded-md mt-1"
        >
          <div class="w-1/6 min-w-min me-1 logo-team-container">
            <NuxtImg
              v-if="ranking?.team?.logo?.url"
              :src="setAliasUrl(ranking.team.logo.url)"
              alt="Team"
              class="object-contain h-8 w-8 lg:w-10 lg:h-10"
              loading="lazy"
              height="40"
              width="40"
            />
            <span v-else class="text-gray-700"
              ><placeholder-team-shield :width="40" :height="40"></placeholder-team-shield
            ></span>
          </div>
          <component
            :is="isStats ? 'h2' : 'div'"
            class="w-4/6 text-left rtl:text-right me-auto text-xs sm:text-sm !leading-[1.1]"
          >
            {{ ranking?.player?.shortName || $t("general.player.word") }}
          </component>
          <div class="w-1/6 flex items-center justify-center">
            {{ ranking && ranking.total ? Number(ranking.total) : 0 }}
          </div>
        </div>
      </div>
    </nuxt-link>

    <div class="text-center py-2" v-if="regionParam && pageStatsLink">
      <nuxt-link
        :to="
          localePath({
            name: `region-stats-${pageStatsLink}`,
            params: { region: regionParam },
            query: queries,
          })
        "
        class="see-more-link"
        :no-prefetch="!!isRegionHome"
        >{{ $t("cards.stats.seeMore.link") }}
      </nuxt-link>
    </div>
  </div>
</template>

<style scoped>
.card-player-image-container {
  @apply min-h-[463px] sm:min-h-[546px] px-1 py-2 rounded-lg relative overflow-hidden bg-no-repeat bg-cover bg-center;
}

.image-container {
  @apply h-full absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-start items-center pt-5;
}

.gradient-container {
  @apply absolute min-w-full min-h-full top-0 right-0 left-0 bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.0)40%,rgba(0,0,0,1)100%)] px-1 py-2 flex flex-col justify-between;
}

.logo-team-container {
  @apply flex flex-col justify-center items-center bigsm:p-0.5;
}

.see-more-link {
  @apply text-xs text-gray-400;
}

.turn-text {
  @apply text-primary text-xs uppercase font-bold leading-[15px] rotate-180;
  writing-mode: vertical-lr;
}
</style>
